import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Code2,
  Play,
  Loader2,
  Download,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  FileCode,
  TrendingUp,
  Filter,
  Database,
  Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ABAPCodeAnalyzer = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [useMockData, setUseMockData] = useState(true);
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [selectedObject, setSelectedObject] = useState(null);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/code-analysis/latest`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.log('No code analysis yet');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/code-analysis/run`, 
        { use_mock_data: useMockData }, 
        
      );
      setAnalysis(response.data);
      setShowDataSourceModal(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Error running code analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const exportBacklog = () => {
    if (!analysis) return;

    const csv = [
      ['Backlog ID', 'Object', 'Type', 'Package', 'Module', 'Priority', 'Criticality', 'Issues', 'Fix Approach', 'Effort', 'Days'].join(','),
      ...analysis.remediation_backlog.map(b => [
        b.backlog_id,
        b.object_name,
        b.object_type,
        b.package,
        b.module,
        b.priority,
        b.criticality,
        b.issue_count,
        b.fix_approach,
        b.effort_band,
        b.estimated_days
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AURA_Code_Remediation_Backlog_${project.name.replace(/\s/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getPriorityColor = (priority) => {
    if (priority === 'P0') return 'bg-red-600 text-white';
    if (priority === 'P1') return 'bg-orange-500 text-white';
    return 'bg-yellow-500 text-white';
  };

  const getSeverityColor = (severity) => {
    if (severity === 'CRITICAL') return 'bg-red-600 text-white';
    if (severity === 'HIGH') return 'bg-orange-500 text-white';
    if (severity === 'MEDIUM') return 'bg-yellow-500 text-white';
    return 'bg-blue-500 text-white';
  };

  const filteredBacklog = analysis?.remediation_backlog.filter(b => {
    if (filterPriority !== 'all' && b.priority !== filterPriority) return false;
    if (filterModule !== 'all' && b.module !== filterModule) return false;
    return true;
  }) || [];

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">ABAP Custom Code Analyzer</h2>
            <p className="text-gray-600">Deep static analysis with AI-powered remediation suggestions</p>
            
            {/* SAP System Indicator */}
            {analysis && analysis.sap_system_analyzed && analysis.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg">
                  <Code2 className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-purple-600">{analysis.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({analysis.sap_system_analyzed.host})</span>
                </div>
                {analysis.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {analysis && (
              <button 
                onClick={exportBacklog}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Export Backlog</span>
              </button>
            )}
            <button 
              onClick={() => setShowDataSourceModal(true)}
              disabled={analyzing}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Run Analysis</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Data Source Modal */}
      {showDataSourceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-6">Select Code Source</h3>
            
            <div className="space-y-4">
              <div 
                onClick={() => setUseMockData(true)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  useMockData ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Database className="w-6 h-6 text-purple-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Mock ABAP Code</p>
                    <p className="text-sm text-gray-600">Use sample code for demonstration</p>
                  </div>
                </div>
              </div>

              <div 
                onClick={() => setUseMockData(false)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  !useMockData ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Zap className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Real SAP System</p>
                    <p className="text-sm text-gray-600">Fetch from configured SAP connection</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button 
                onClick={runAnalysis}
                disabled={analyzing}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 disabled:opacity-50"
              >
                {analyzing ? 'Analyzing...' : 'Run Analysis'}
              </button>
              <button 
                onClick={() => setShowDataSourceModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!analysis && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Code2 className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Code Analysis</h3>
          <p className="text-gray-600 mb-6">Run deep static analysis on custom ABAP code</p>
          <button 
            onClick={() => setShowDataSourceModal(true)}
            className="bg-purple-600 text-white px-8 py-3 rounded-xl hover:bg-purple-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Analysis</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {analysis && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <Code2 className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.total_objects_analyzed}</div>
              <div className="text-purple-100 text-sm">Objects Analyzed</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.total_issues}</div>
              <div className="text-red-100 text-sm">Issues Found</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <Sparkles className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.ai_suggestions_count}</div>
              <div className="text-orange-100 text-sm">AI Suggestions</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <FileCode className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.pattern_matches_count}</div>
              <div className="text-blue-100 text-sm">Pattern Matches</div>
            </div>
          </div>

          {/* Data Source Badge */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Data Source:</span> {analysis.data_source}
            </p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'objects', label: 'Analyzed Objects', icon: Code2 },
                { id: 'backlog', label: 'Remediation Backlog', icon: FileCode },
                { id: 'patterns', label: 'Pattern Fixes', icon: CheckCircle2 },
                { id: 'ai-fixes', label: 'AI Suggestions', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Issue Statistics */}
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Critical Issues</h4>
                      <div className="text-4xl font-bold text-red-600">{analysis.summary.critical_issues}</div>
                      <p className="text-sm text-gray-600 mt-2">Require immediate attention</p>
                    </div>
                    <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">High Priority</h4>
                      <div className="text-4xl font-bold text-orange-600">{analysis.summary.high_issues}</div>
                      <p className="text-sm text-gray-600 mt-2">Plan for next sprint</p>
                    </div>
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Total Issues</h4>
                      <div className="text-4xl font-bold text-blue-600">{analysis.summary.total_issues}</div>
                      <p className="text-sm text-gray-600 mt-2">Across all objects</p>
                    </div>
                  </div>

                  {/* Remediation Backlog Summary */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Remediation Backlog Priority</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-3xl font-bold text-red-600">{analysis.summary.p0_items}</div>
                        <div className="text-sm text-gray-600">P0 - Critical</div>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-3xl font-bold text-orange-600">{analysis.summary.p1_items}</div>
                        <div className="text-sm text-gray-600">P1 - High</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">
                          {analysis.remediation_backlog.length - analysis.summary.p0_items - analysis.summary.p1_items}
                        </div>
                        <div className="text-sm text-gray-600">P2 - Medium</div>
                      </div>
                    </div>
                  </div>

                  {/* Fix Approach Distribution */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Fix Approach Distribution</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      {['AI-Assisted', 'Pattern-Based', 'Manual'].map(approach => {
                        const count = analysis.remediation_backlog.filter(b => b.fix_approach === approach).length;
                        const percentage = (count / analysis.remediation_backlog.length * 100).toFixed(0);
                        return (
                          <div key={approach} className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">{count}</div>
                            <div className="text-sm text-gray-600">{approach}</div>
                            <div className="text-xs text-gray-500 mt-1">{percentage}%</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Analyzed Objects Tab */}
              {activeTab === 'objects' && (
                <div className="space-y-4">
                  {analysis.analyzed_objects.map((obj) => (
                    <div 
                      key={obj.object_name}
                      onClick={() => setSelectedObject(obj)}
                      className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getPriorityColor(obj.priority)}`}>
                              {obj.priority}
                            </span>
                            <h4 className="text-lg font-bold text-gray-900">{obj.object_name}</h4>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs mb-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {obj.object_type}
                            </span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {obj.package}
                            </span>
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                              {obj.business_module}
                            </span>
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              {obj.lines_of_code} LOC
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-2xl font-bold text-orange-600">{obj.criticality}</div>
                          <div className="text-xs text-gray-500">Criticality</div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Issues:</span>
                          <span className="font-bold">{obj.issue_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Critical:</span>
                          <span className="font-bold text-red-600">{obj.critical_issues}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">High:</span>
                          <span className="font-bold text-orange-600">{obj.high_issues}</span>
                        </div>
                      </div>

                      {obj.issues.length > 0 && (
                        <div className="mt-4 bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-2">Top Issues:</p>
                          {obj.issues.slice(0, 3).map((issue, idx) => (
                            <div key={idx} className="text-sm text-gray-700 mb-1 flex items-start space-x-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${getSeverityColor(issue.severity)}`}>
                                {issue.severity}
                              </span>
                              <span>{issue.description}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Remediation Backlog Tab */}
              {activeTab === 'backlog' && (
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select 
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Priorities</option>
                      <option value="P0">P0 - Critical</option>
                      <option value="P1">P1 - High</option>
                      <option value="P2">P2 - Medium</option>
                    </select>
                    <select 
                      value={filterModule}
                      onChange={(e) => setFilterModule(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Modules</option>
                      {['FI', 'CO', 'MM', 'SD', 'PP', 'HCM', 'Cross'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-600">{filteredBacklog.length} items</span>
                  </div>

                  {/* Backlog Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 px-4 py-3 text-left font-bold">Object</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Priority</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Criticality</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Issues</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Fix Approach</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Effort</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Est. Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBacklog.map((item) => (
                          <tr key={item.backlog_id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-3">
                              <div className="font-semibold">{item.object_name}</div>
                              <div className="text-xs text-gray-600">{item.package} • {item.module}</div>
                            </td>
                            <td className="border border-gray-300 px-4 py-3 text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${getPriorityColor(item.priority)}`}>
                                {item.priority}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-4 py-3 text-center">
                              <span className="font-bold text-orange-600">{item.criticality}</span>
                            </td>
                            <td className="border border-gray-300 px-4 py-3 text-center">
                              <span className="font-bold">{item.issue_count}</span>
                              {item.critical_issues > 0 && (
                                <span className="text-xs text-red-600 ml-1">({item.critical_issues} critical)</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                item.fix_approach === 'AI-Assisted' ? 'bg-purple-100 text-purple-700' :
                                item.fix_approach === 'Pattern-Based' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {item.fix_approach}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-4 py-3 text-center">
                              <span className="font-bold">{item.effort_band}</span>
                            </td>
                            <td className="border border-gray-300 px-4 py-3 text-center">
                              <span className="font-bold">{item.estimated_days}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pattern Fixes Tab */}
              {activeTab === 'patterns' && (
                <div className="space-y-4">
                  {analysis.pattern_matches.map((match, idx) => (
                    <div key={idx} className="bg-white border-2 border-blue-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{match.pattern_name}</h4>
                          <p className="text-sm text-gray-600">Object: {match.object_name}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          match.auto_fix === 'safe' ? 'bg-green-600 text-white' :
                          match.auto_fix === 'semi-automatic' ? 'bg-yellow-600 text-white' :
                          'bg-red-600 text-white'
                        }`}>
                          {match.auto_fix.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-xs font-semibold text-gray-700 mb-2">❌ Before (Anti-Pattern)</p>
                          <pre className="text-xs text-gray-800 overflow-x-auto font-mono">{match.before_example}</pre>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-xs font-semibold text-gray-700 mb-2">✅ After (Recommended)</p>
                          <pre className="text-xs text-gray-800 overflow-x-auto font-mono">{match.after_example}</pre>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between text-sm">
                        <span className="text-gray-600">Effort Saving: <span className="font-semibold">{match.effort_saving}</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* AI Suggestions Tab */}
              {activeTab === 'ai-fixes' && (
                <div className="space-y-4">
                  {analysis.ai_suggestions.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>No AI suggestions generated yet</p>
                    </div>
                  )}

                  {analysis.ai_suggestions.map((suggestion, idx) => (
                    <div key={idx} className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{suggestion.object_name}</h4>
                          <p className="text-sm text-gray-600">Issue: {suggestion.issue_id}</p>
                        </div>
                        <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center space-x-1">
                          <Sparkles className="w-3 h-3" />
                          <span>{suggestion.generated_by}</span>
                        </span>
                      </div>

                      <div className="bg-white border border-purple-200 rounded-lg p-4 mb-4">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Suggested Code Fix</p>
                        <pre className="text-sm text-gray-800 overflow-x-auto font-mono bg-gray-50 p-3 rounded">
                          {suggestion.suggested_code}
                        </pre>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">Explanation:</p>
                          <p className="text-sm text-gray-700">{suggestion.explanation}</p>
                        </div>

                        {suggestion.caveats && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-sm font-semibold text-gray-900 mb-1 flex items-center space-x-2">
                              <AlertTriangle className="w-4 h-4 text-yellow-600" />
                              <span>Caveats:</span>
                            </p>
                            <p className="text-sm text-gray-700">{suggestion.caveats}</p>
                          </div>
                        )}

                        {suggestion.test_approach && (
                          <div>
                            <p className="text-sm font-semibold text-gray-900 mb-1">Test Approach:</p>
                            <p className="text-sm text-gray-700">{suggestion.test_approach}</p>
                          </div>
                        )}

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <p className="text-xs font-bold text-orange-700">
                            ⚠️ Confidence: {suggestion.confidence.toUpperCase()} - Manual review required
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Object Detail Modal */}
          {selectedObject && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold">{selectedObject.object_name}</h3>
                    <p className="text-gray-600">{selectedObject.package} • {selectedObject.business_module}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedObject(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600">Lines of Code</p>
                      <p className="text-xl font-bold">{selectedObject.lines_of_code}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600">Issues</p>
                      <p className="text-xl font-bold">{selectedObject.issue_count}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600">Criticality</p>
                      <p className="text-xl font-bold text-orange-600">{selectedObject.criticality}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600">Priority</p>
                      <p className="text-xl font-bold">{selectedObject.priority}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Issues Detected</h4>
                    <div className="space-y-2">
                      {selectedObject.issues.map((issue, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(issue.severity)}`}>
                              {issue.severity}
                            </span>
                            <span className="text-xs text-gray-500">Line {issue.line_number}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">{issue.issue_type}</p>
                          <p className="text-sm text-gray-700">{issue.description}</p>
                          <p className="text-xs text-gray-500 mt-2">Phase Impact: {issue.phase_impact}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ABAPCodeAnalyzer;
